import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ViewPreferences {
  pageSize: number;
  pageNumber: number;
  statusFilter?: string;
  searchTerm?: string;
  sortBy?: string;
  orderBy?: string;
  selectedColumns?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class ViewPreferencesCacheService {
  private cache = new Map<string, ViewPreferences>();
  private cacheSubject = new BehaviorSubject<Map<string, ViewPreferences>>(this.cache);
  private readonly CACHE_KEY = 'view-preferences-cache';
  private readonly CACHE_EXPIRY_KEY = 'view-preferences-cache-expiry';
  private readonly CACHE_DURATION = 1 * 60 * 60 * 1000;

  constructor() {
    this.loadCacheFromStorage();
    this.setupCleanupInterval();
    this.setupRefreshDetection();
  }

  savePreferences(routeKey: string, preferences: ViewPreferences): void {
    this.cache.set(routeKey, { ...preferences });
    this.saveCacheToStorage();
    this.cacheSubject.next(this.cache);
  }

  getPreferences(routeKey: string): ViewPreferences | null {
    return this.cache.get(routeKey) || null;
  }

  getPreferencesWithDefaults(routeKey: string, defaultPreferences: ViewPreferences): ViewPreferences | null {
    const cached = this.cache.get(routeKey);
    if (cached) {
      return { ...defaultPreferences, ...cached };
    }
    return null;
  }

  clearPreferences(routeKey: string): void {
    this.cache.delete(routeKey);
    this.saveCacheToStorage();
    this.cacheSubject.next(this.cache);
  }


  clearAllPreferences(): void {
    this.cache.clear();
    this.saveCacheToStorage();
    this.cacheSubject.next(this.cache);
  }


  clearCacheOnRefresh(): void {
    this.clearAllPreferences();
    sessionStorage.removeItem('cache-refresh-detected');
  }

  getCacheObservable(): Observable<Map<string, ViewPreferences>> {
    return this.cacheSubject.asObservable();
  }


  private loadCacheFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.CACHE_KEY);
      const expiry = localStorage.getItem(this.CACHE_EXPIRY_KEY);
      
      if (stored && expiry) {
        const now = Date.now();
        const expiryTime = parseInt(expiry, 10);
        
        if (now < expiryTime) {
          const parsedCache = JSON.parse(stored);
          this.cache = new Map(Object.entries(parsedCache));
          this.cacheSubject.next(this.cache);
        } else {
          this.clearAllPreferences();
        }
      }
    } catch (error) {
      console.error('Error loading cache from storage:', error);
      this.clearAllPreferences();
    }
  }


  private saveCacheToStorage(): void {
    try {
      const cacheObject = Object.fromEntries(this.cache);
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheObject));
      localStorage.setItem(this.CACHE_EXPIRY_KEY, (Date.now() + this.CACHE_DURATION).toString());
    } catch (error) {
      console.error('Error saving cache to storage:', error);
    }
  }


  private setupCleanupInterval(): void {
    setInterval(() => {
      const now = Date.now();
      const expiry = localStorage.getItem(this.CACHE_EXPIRY_KEY);
      
      if (expiry && now >= parseInt(expiry, 10)) {
        this.clearAllPreferences();
      }
    }, 60 * 60 * 1000);
  }


  private setupRefreshDetection(): void {
    if (window.performance && window.performance.navigation) {
      const navigationType = window.performance.navigation.type;
      if (navigationType === 1) {
        this.clearAllPreferences();
        return;
      }
    }

    const isRefresh = sessionStorage.getItem('cache-refresh-detected');
    if (isRefresh) {
      this.clearAllPreferences();
      sessionStorage.removeItem('cache-refresh-detected');
    } else {
      sessionStorage.setItem('cache-refresh-detected', 'true');
    }


    window.addEventListener('beforeunload', () => {
      sessionStorage.setItem('cache-refresh-detected', 'true');
    });
  }
}
